import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidatePhotoQueries } from "@/lib/mutationHelpers";

export interface TaskReferencePhoto {
  id: string;
  property_id: string;
  room_name: string;
  task_text: string;
  photo_url: string;
  display_order: number;
  created_at: string;
  checklist_id?: string | null;
}

export type TaskReferencePhotosResult = {
  list: TaskReferencePhoto[];
  hasLegacyFallback: boolean;
};

export const useTaskReferencePhotos = (
  propertyId: string | undefined,
  checklistId: string | null | undefined = undefined
) => {
  return useQuery({
    queryKey: queryKeys.photos.taskReference(propertyId || '', checklistId ?? null),
    queryFn: async (): Promise<TaskReferencePhotosResult> => {
      if (!propertyId) return { list: [], hasLegacyFallback: false };
      const key = checklistId ?? null;

      if (key != null) {
        const { data: scoped, error: e1 } = await supabase
          .from("task_reference_photos")
          .select("*")
          .eq("property_id", propertyId)
          .eq("checklist_id", key)
          .order("room_name", { ascending: true })
          .order("task_text", { ascending: true })
          .order("display_order", { ascending: true });
        if (e1) throw e1;
        if (scoped && scoped.length > 0) return { list: scoped as TaskReferencePhoto[], hasLegacyFallback: false };

        const { data: legacy, error: e2 } = await supabase
          .from("task_reference_photos")
          .select("*")
          .eq("property_id", propertyId)
          .is("checklist_id", null)
          .order("room_name", { ascending: true })
          .order("task_text", { ascending: true })
          .order("display_order", { ascending: true });
        if (e2) throw e2;
        return { list: (legacy ?? []) as TaskReferencePhoto[], hasLegacyFallback: true };
      }

      const { data, error } = await supabase
        .from("task_reference_photos")
        .select("*")
        .eq("property_id", propertyId)
        .is("checklist_id", null)
        .order("room_name", { ascending: true })
        .order("task_text", { ascending: true })
        .order("display_order", { ascending: true });
      if (error) throw error;
      return { list: (data ?? []) as TaskReferencePhoto[], hasLegacyFallback: false };
    },
    enabled: !!propertyId,
  });
};

// Get all photos for a specific task (accepts list array from useTaskReferencePhotos result)
export const getPhotosForTask = (
  photos: TaskReferencePhoto[] | undefined,
  roomName: string,
  taskText: string
): TaskReferencePhoto[] => {
  if (!photos) return [];
  return photos.filter(
    (p) => p.room_name === roomName && p.task_text === taskText
  );
};

export const useUploadTaskReferencePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      roomName,
      taskText,
      file,
      displayOrder = 0,
      checklistId = null,
    }: {
      propertyId: string;
      roomName: string;
      taskText: string;
      file: File;
      displayOrder?: number;
      checklistId?: string | null;
    }) => {
      const roomSlug = roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
      const taskSlug = taskText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "").slice(0, 30);
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const filePath = `${propertyId}/${roomSlug}/${taskSlug}-${timestamp}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("reference-photos")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("reference-photos")
        .getPublicUrl(filePath);

      const row: { property_id: string; room_name: string; task_text: string; photo_url: string; display_order: number; checklist_id?: string | null } = {
        property_id: propertyId,
        room_name: roomName,
        task_text: taskText,
        photo_url: urlData.publicUrl,
        display_order: displayOrder,
      };
      if (checklistId != null) row.checklist_id = checklistId;

      const { data, error: dbError } = await supabase
        .from("task_reference_photos")
        .insert(row)
        .select()
        .single();

      if (dbError) throw dbError;

      return data as TaskReferencePhoto;
    },
    onSuccess: (_, variables) => {
      invalidatePhotoQueries(queryClient, variables.propertyId, variables.checklistId ?? null);
    },
  });
};

export const useUploadMultipleTaskReferencePhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      roomName,
      taskText,
      files,
      existingPhotosCount = 0,
      checklistId = null,
    }: {
      propertyId: string;
      roomName: string;
      taskText: string;
      files: File[];
      existingPhotosCount?: number;
      checklistId?: string | null;
    }) => {
      const roomSlug = roomName.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "");
      const taskSlug = taskText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "").slice(0, 30);
      const uploadedPhotos: TaskReferencePhoto[] = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const timestamp = Date.now() + i;
        const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const filePath = `${propertyId}/${roomSlug}/${taskSlug}-${timestamp}.${fileExt}`;
        const displayOrder = existingPhotosCount + i;

        const { error: uploadError } = await supabase.storage
          .from("reference-photos")
          .upload(filePath, file, { upsert: true });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("reference-photos")
          .getPublicUrl(filePath);

        const row: { property_id: string; room_name: string; task_text: string; photo_url: string; display_order: number; checklist_id?: string | null } = {
          property_id: propertyId,
          room_name: roomName,
          task_text: taskText,
          photo_url: urlData.publicUrl,
          display_order: displayOrder,
        };
        if (checklistId != null) row.checklist_id = checklistId;

        const { data, error: dbError } = await supabase
          .from("task_reference_photos")
          .insert(row)
          .select()
          .single();

        if (dbError) throw dbError;

        uploadedPhotos.push(data as TaskReferencePhoto);
      }

      return uploadedPhotos;
    },
    onSuccess: (_, variables) => {
      invalidatePhotoQueries(queryClient, variables.propertyId, variables.checklistId ?? null);
    },
  });
};

export const useDeleteTaskReferencePhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      photoUrl,
      checklistId = null,
    }: {
      id: string;
      propertyId: string;
      photoUrl: string;
      checklistId?: string | null;
    }) => {
      const { error: dbError } = await supabase
        .from("task_reference_photos")
        .delete()
        .eq("id", id);

      if (dbError) throw dbError;

      try {
        const url = new URL(photoUrl);
        const pathParts = url.pathname.split("/reference-photos/");
        if (pathParts[1]) {
          await supabase.storage.from("reference-photos").remove([pathParts[1]]);
        }
      } catch (e) {
        console.warn("Could not delete file from storage:", e);
      }

      return { propertyId, checklistId: checklistId ?? null };
    },
    onSuccess: (data) => {
      invalidatePhotoQueries(queryClient, data.propertyId, data.checklistId);
    },
  });
};

// Delete all photos for a specific task (scoped by checklist when provided)
export const useDeleteAllTaskReferencePhotos = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      roomName,
      taskText,
      photos,
      checklistId = null,
    }: {
      propertyId: string;
      roomName: string;
      taskText: string;
      photos: TaskReferencePhoto[];
      checklistId?: string | null;
    }) => {
      let q = supabase
        .from("task_reference_photos")
        .delete()
        .eq("property_id", propertyId)
        .eq("room_name", roomName)
        .eq("task_text", taskText);
      if (checklistId != null) q = q.eq("checklist_id", checklistId);
      else q = q.is("checklist_id", null);
      const { error: dbError } = await q;

      if (dbError) throw dbError;

      for (const photo of photos) {
        try {
          const url = new URL(photo.photo_url);
          const pathParts = url.pathname.split("/reference-photos/");
          if (pathParts[1]) {
            await supabase.storage.from("reference-photos").remove([pathParts[1]]);
          }
        } catch (e) {
          console.warn("Could not delete file from storage:", e);
        }
      }

      return { propertyId, checklistId: checklistId ?? null };
    },
    onSuccess: (data) => {
      invalidatePhotoQueries(queryClient, data.propertyId, data.checklistId);
    },
  });
};
